/**
 * **La conversation AFFICHÉE** — celle que l'avocat a sous les yeux, où qu'il
 * soit dans l'application (#859).
 *
 * Pourquoi ce module existe : jusqu'ici cette grandeur n'était nulle part. Elle
 * était approchée par une `sessionKey` locale à l'écran assistant
 * (`BackgroundSessionNotifier`), ce que la connaissance du module interdit par
 * ailleurs — *une clé seule n'est jamais une identité* : après une navigation
 * A→B, la clé peut encore valoir A pendant un rendu où l'URL vaut B.
 *
 * **Trois grandeurs distinctes, à ne jamais confondre** :
 * - la conversation *affichée* — ce que l'avocat regarde (ce module) ;
 * - le *porteur* du canvas — à qui appartient le document ouvert
 *   (`redaction/state/canvasHolder.ts`, #862) ;
 * - la conversation *d'un travail* — qui reçoit son annonce de fin
 *   (`redaction/streaming/redactionStreamHub.ts`, #857).
 *
 * Elles coïncidaient tant que tout vivait dans l'écran assistant. Elles
 * divergent depuis #856, et c'est exactement cette divergence que #859 doit
 * rendre visible à l'avocat.
 *
 * **Trois écrans publient, et eux seuls** : l'assistant (`AgentV3View`, depuis
 * l'URL `?c=`), le chat embarqué du dossier (`DossierCaseDiscoveryShell`, qui
 * choisit son fil localement sans toucher à l'URL) et le chat de numérotation
 * (`AiNumberingChat`, dans le wizard bordereau). Un quatrième écran
 * qui afficherait un fil devrait publier ici, sinon l'avocat sera prévenu d'une
 * fin de tour qu'il est en train de regarder. S'y ajoutent des **gestes**
 * impératifs (clic de la sidebar, conversation créée, bascule du dock vers
 * l'assistant — #1587/#1588) qui publient sans être des écrans.
 *
 * Le registre vit **hors de React** parce que ses lecteurs y vivent aussi : le
 * garde de fermeture d'onglet et l'avertisseur de fin de tour s'abonnent à des
 * événements, pas à des rendus.
 */

import * as React from 'react';

type Abonne = (conversationId: string | null) => void;

/**
 * Identité d'un publieur (#1587). Deux écrans peuvent afficher le MÊME fil — le
 * dock et l'assistant pendant la bascule de l'un vers l'autre : une valeur ne dit
 * donc pas qui l'a posée, et seul celui qui l'a posée en dernier peut l'effacer.
 */
export type JetonDePublication = symbol;

let conversationAffichee: string | null = null;
/** Dernier écran à avoir publié ; `null` quand la valeur vient d'un geste anonyme. */
let proprietaire: JetonDePublication | null = null;
const abonnes = new Set<Abonne>();

/**
 * Jeton de la relève : aucun écran ne le détient, donc personne ne le retire.
 * Seul le prochain publieur le remplace.
 */
const RELEVE: JetonDePublication = Symbol('conversationAffichee.releve');

/** Ce que l'avocat regarde en ce moment, ou `null` s'il ne regarde aucun fil. */
export function lireConversationAffichee(): string | null {
  return conversationAffichee;
}

/** Être prévenu que l'avocat a changé de fil — ou n'en regarde plus aucun. */
export function souscrireConversationAffichee(abonne: Abonne): () => void {
  abonnes.add(abonne);
  return () => {
    abonnes.delete(abonne);
  };
}

function notifier(conversationId: string | null): void {
  for (const abonne of [...abonnes]) abonne(conversationId);
}

/**
 * Publier. Exporté pour les gestes impératifs et les tests ; les écrans passent
 * par `usePublierConversationAffichee`, qui fournit son jeton.
 *
 * - **Avec jeton** : le publieur devient propriétaire même si la valeur ne
 *   change pas. C'est le cœur du correctif #1587 : l'assistant qui monte sur le
 *   fil que le dock affichait déjà doit reprendre la main, sinon le démontage du
 *   dock effacerait ce que l'assistant montre.
 * - **Sans jeton, même valeur** : rien. Le clic de la sidebar republie le fil
 *   déjà affiché ; il ne doit pas priver l'écran de son droit de le reposer.
 * - **Sans jeton, autre valeur** : le geste supplante l'écran, qui ne pourra
 *   plus rien effacer en partant.
 *
 * Les abonnés ne sont réveillés que sur un vrai changement de valeur : un
 * transfert de propriété n'est pas un changement de fil.
 */
export function publierConversationAffichee(
  conversationId: string | null,
  jeton?: JetonDePublication,
): void {
  if (jeton !== undefined) {
    proprietaire = jeton;
  } else if (conversationAffichee !== conversationId) {
    proprietaire = null;
  }
  if (conversationAffichee === conversationId) return;
  conversationAffichee = conversationId;
  notifier(conversationId);
}

/**
 * Reposer à `null` ce que `jeton` a publié — et rien d'autre. Si un autre
 * publieur (écran, geste ou relève) est passé depuis, l'appel est sans effet.
 */
export function retirerConversationAffichee(jeton: JetonDePublication): void {
  if (proprietaire !== jeton) return;
  proprietaire = null;
  if (conversationAffichee === null) return;
  conversationAffichee = null;
  notifier(null);
}

/**
 * Tenir le fil pendant une bascule d'écran (#1587).
 *
 * La bascule vers l'assistant ferme le dock puis navigue, et React commite les
 * deux en deux temps : le dock démonté d'abord (fermeture synchrone), la page
 * `/assistant` ensuite (navigation en transition). Entre les deux, le dock ne
 * peut plus effacer le fil — il n'en est plus propriétaire — et l'assistant le
 * reprend en publiant. Sans relève, le registre tomberait à `null` dans cet
 * intervalle : le garde de départ croirait que l'avocat quitte un fil qui rédige
 * et l'avertisseur annoncerait « Réponse prête » pour le fil qu'il va regarder.
 *
 * Limite assumée : si la navigation est supplantée avant que l'assistant ne
 * monte, le fil reste publié jusqu'au prochain publieur — comme après un clic
 * de la sidebar.
 */
export function releverConversationAffichee(conversationId: string): void {
  publierConversationAffichee(conversationId, RELEVE);
}

/** Remise à zéro complète — tests et rechargement à chaud. */
export function reinitialiserConversationAffichee(): void {
  conversationAffichee = null;
  proprietaire = null;
  abonnes.clear();
}

/**
 * Lire le fil affiché depuis React.
 *
 * `useSyncExternalStore` est sûr ici — et ne l'aurait pas été pour la liste des
 * conversations qui rédigent — parce que l'instantané est une **valeur
 * primitive** : il ne reconstruit aucun objet à chaque lecture, donc il ne peut
 * pas violer le contrat de stabilité de `getSnapshot`.
 */
export function useConversationAffichee(): string | null {
  return React.useSyncExternalStore(
    souscrireConversationAffichee,
    lireConversationAffichee,
    lireConversationAffichee,
  );
}

/**
 * Déclarer, depuis un écran, le fil qu'il affiche.
 *
 * `useLayoutEffect` et non `useEffect` : les lecteurs sont hors de React et
 * consomment cette valeur sur un événement réseau, qui peut tomber juste après
 * le commit — même raison que la publication de la scène du canvas (#862).
 *
 * **Le démontage repose à `null`, mais seulement s'il n'a pas été supplanté** —
 * c'est-à-dire si aucun autre jeton n'a publié après lui. React monte l'écran
 * suivant avant de nettoyer le précédent : un effacement inconditionnel
 * effacerait la déclaration de celui qui vient d'arriver, et l'avocat recevrait
 * une notification pour le fil qu'il a devant lui. Même piège que la libération
 * du nœud hôte du canvas (`releaseCanvasHost`, #856).
 *
 * Le jeton, et non la valeur, décide (#1587) : le dock et l'assistant publient
 * le même identifiant pendant la bascule vers l'assistant, et un nettoyage par
 * valeur laissait le premier démonté effacer le fil que le second affiche.
 * `useState` et non `useRef` : l'initialiseur paresseux donne un jeton stable
 * pour toute la vie de l'écran, StrictMode compris.
 */
export function usePublierConversationAffichee(conversationId: string | null): void {
  const [jeton] = React.useState<JetonDePublication>(() => Symbol('conversationAffichee'));
  React.useLayoutEffect(() => {
    publierConversationAffichee(conversationId, jeton);
    return () => {
      retirerConversationAffichee(jeton);
    };
  }, [conversationId, jeton]);
}
